import styled from '@emotion/styled';
import { up } from '../../../../style-utils/media-queries';
import { layout, palette, radius, shadow, spacing, transition, typography } from '../../../../style-utils/theme';

/* Independent Stays IS2 — the Properties page and the Add-a-property form.
   Tokens only (design_system.md §9, UI-CONTRACT §1). Mobile first. */

export const Section = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

export const SectionTitle = styled.legend`
  margin: 0 0 ${spacing.xs};
  padding: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  min-width: 0;
`;

/* The three kinds of property: large choice cards, one radio group. */
export const KindGroup = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.xs};

  ${up('md')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const KindCard = styled.button<{ $checked: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  min-width: 0;
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm};
  border: 1px solid ${p => (p.$checked ? palette.iris : palette.line)};
  border-radius: ${radius.sm};
  background: ${p => (p.$checked ? palette.irisTintSoft : palette.surface)};
  box-shadow: ${shadow.xs};
  font-family: ${typography.ui};
  text-align: left;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.iris};
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const KindTitle = styled.span`
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const KindHint = styled.span`
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const Rows = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const RowLine = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Inline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

export const Small = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const Statement = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  cursor: pointer;

  & input {
    width: 20px;
    height: 20px;
    margin: 2px 0 0;
  }
`;

export const PasteArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${palette.iris};
    box-shadow: ${shadow.focus};
  }
`;

/* ── the Properties page ── */

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding-bottom: ${layout.fabReserve};
`;

export const PageHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

export const CardList = styled.ul`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;

  ${up('lg')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const PropertyCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const CardMeta = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export const UnitList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const UnitRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  min-height: ${layout.touchTarget};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};

  & a {
    color: ${palette.irisDeep};
    overflow-wrap: anywhere;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;
